// src/pages/fleet/IfmsQueuePage.tsx — "IFMS Queue": every started trip must reach
// IFMS within the SLA (24 h by default). Anything still pending is retried by the
// server on its own; this screen shows what is stuck, why, and how long is left.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIfmsQueue, runIfmsQueue, getIfmsEvent, reconcileIfms } from '../../services/fleetApi';
import type { IfmsQueueItem, IfmsQueueSummary, IfmsReasonRow, IfmsRegionRow, IfmsEvent } from '../../services/fleetApi';
import { P } from './fleetStyles';
import { Modal, RCol, ReportShell, ReportTable, Tag, fmtDT, fmtDur } from './reports/reportKit';
import { apiError } from '../../utils/errors';
import { useAuth } from '../../store/AuthContext';
import { can } from './permissions';

// The wording matters more than it looks: an operator has to be able to tell at a
// glance which of these the system will handle on its own (retrying) and which
// are waiting for them (on hold, needs checking). FAILED and QUEUED only appear
// on rows written before the queue was reworked.
const STATUS_TONE = {
  SUCCESS: 'green', RETRYING: 'orange', UNKNOWN_OUTCOME: 'red', ON_HOLD: 'red',
  PROCESSING: 'gray', NOT_SENT: 'gray', FAILED: 'red', QUEUED: 'gray',
} as const;
const STATUS_TEXT = {
  SUCCESS: 'sent', RETRYING: 'retrying', UNKNOWN_OUTCOME: 'needs checking', ON_HOLD: 'on hold',
  PROCESSING: 'sending', NOT_SENT: 'waiting', FAILED: 'failed', QUEUED: 'queued',
} as const;

/** "4h 20m left" / "3h 10m overdue" — the number that matters on this screen. */
function dueText(i: IfmsQueueItem) {
  const mins = Math.round(Math.abs(i.hoursLeft) * 60);
  return i.overdue ? `${fmtDur(mins)} overdue` : `${fmtDur(mins)} left`;
}
const dueColor = (i: IfmsQueueItem) =>
  (i.overdue ? 'var(--fx-red)' : i.hoursLeft <= 6 ? 'var(--fx-orange)' : 'var(--fx-green)');

export default function IfmsQueuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRun = can(user, 'trip_list');
  const [items, setItems] = useState<IfmsQueueItem[]>([]);
  const [regions, setRegions] = useState<IfmsRegionRow[]>([]);
  const [reasons, setReasons] = useState<IfmsReasonRow[]>([]);
  const [summary, setSummary] = useState<IfmsQueueSummary | null>(null);
  // Clicking a region / reason narrows the table below to it.
  const [pick, setPick] = useState<{ kind: 'region' | 'reason'; key: string; label: string } | null>(null);
  const [detail, setDetail] = useState<{ tripId: string; api: IfmsQueueItem['api'] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [running, setRunning] = useState(false);

  const load = useCallback(() => getIfmsQueue()
    .then((r) => {
      setItems(r.data.items || []); setRegions(r.data.regions || []); setReasons(r.data.reasons || []);
      setSummary(r.data.summary); setError('');
    })
    .catch((e) => setError(apiError(e, 'Could not load the IFMS queue')))
    .finally(() => setLoading(false)), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);   // the server retries every 5 min; keep the screen fresh
    return () => clearInterval(t);
  }, [load]);

  const runNow = async () => {
    setRunning(true); setNotice('');
    try {
      const r = await runIfmsQueue();
      setNotice(`Queue run: ${r.data.sent} delivered, ${r.data.failed} still pending.`);
      await load();
    } catch (e) { setError(apiError(e, 'Could not run the queue')); }
    finally { setRunning(false); }
  };

  const COLS: RCol<IfmsQueueItem>[] = [
    { key: 'tripNumber', label: 'Trip', render: (r) => <b style={{ whiteSpace: 'nowrap' }}>{r.tripNumber}</b> },
    { key: 'vehicleNumber', label: 'Vehicle', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{r.vehicleNumber}</span> },
    { key: 'label', label: 'Stage', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{r.label}</span> },
    { key: 'regionPlace', label: 'Destination Region',
      value: (r) => `${r.regionPlace || '—'} (${r.stateCode || '?'}/${r.districtCode || '?'})`,
      render: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {r.regionPlace || '—'}<br />
          <span style={{ fontSize: 11, color: 'var(--fx-text-3)' }}>state {r.stateCode || '?'} · district {r.districtCode || '?'}</span>
        </span>
      ) },
    { key: 'status', label: 'Status', value: (r) => STATUS_TEXT[r.status], render: (r) => <Tag text={STATUS_TEXT[r.status]} tone={STATUS_TONE[r.status]} /> },
    { key: 'attempts', label: 'Tries', align: 'right' },
    { key: 'reason', label: 'Why it is pending', value: (r) => r.reason,
      render: (r) => (
        <span style={{ display: 'inline-block', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom', ...(r.missing.length ? { color: 'var(--fx-orange)', fontWeight: 600 } : {}) }}
          title={`${r.reason}\n\n${r.remarks}`}>{r.reason}</span>
      ) },
    { key: 'dueAt', label: 'Deadline', sortValue: (r) => r.hoursLeft, value: (r) => `${fmtDT(r.dueAt)} (${dueText(r)})`,
      render: (r) => <b style={{ color: dueColor(r), whiteSpace: 'nowrap' }}>{dueText(r)}</b> },
    { key: 'nextRetryAt', label: 'Next Try', value: (r) => (r.nextRetryAt ? fmtDT(r.nextRetryAt) : 'now'),
      render: (r) => <span style={{ whiteSpace: 'nowrap', color: 'var(--fx-text-2)' }}>{r.nextRetryAt ? fmtDT(r.nextRetryAt) : 'now'}</span> },
    { key: 'open', label: '', noExport: true, render: (r) => (
      <span style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
        <button type="button" style={{ ...P.btnGhost, padding: '5px 10px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); setDetail({ tripId: r.tripId, api: r.api }); }}>Details</button>
        <button type="button" style={{ ...P.btnGhost, padding: '5px 10px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); navigate(`/fleet/trips/${r.tripId}`); }}>Fix / View</button>
      </span>
    ) },
  ];

  const shown = pick
    ? items.filter((i) => (pick.kind === 'region' ? i.region === pick.key : i.reasonCode === pick.key))
    : items;

  return (
    <ReportShell icon="shield" tint="#FF9500" title="IFMS Queue"
      subtitle={`Every started trip must reach IFMS within ${summary?.slaHours ?? 24} hours of being created`}
      note={
        <>
          A temporary failure is retried after 2, then 5, then 10 minutes, and every 10 minutes
          after that, for as long as it takes. A report marked <b>on hold</b> is waiting for someone
          to fix data or credentials; one marked <b>needs checking</b> may already have reached IFMS
          and must be confirmed before anything is sent again. Past the deadline, a critical alert is raised —
          the retries carry on regardless.
          {summary && !summary.configured && <> <b>IFMS credentials are not set on this server, so nothing is being sent yet.</b></>}
        </>
      }
      filters={
        <>
          {canRun && (
            <button type="button" style={{ ...P.btnPrimary, opacity: running ? 0.7 : 1 }} disabled={running} onClick={runNow}>
              {running ? 'Running…' : '⟳ Retry all now'}
            </button>
          )}
          <button type="button" style={P.btnGhost} onClick={load}>Refresh</button>
          {notice && <span style={{ fontSize: 12.5, color: 'var(--fx-green)' }}>{notice}</span>}
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--fx-text-3)' }}>Updates every 30 seconds</span>
        </>
      }
      stats={[
        { label: 'Pending', value: summary?.pending ?? '—', tint: 'var(--fx-accent)' },
        { label: 'Past deadline', value: summary?.overdue ?? '—', tint: 'var(--fx-red)' },
        { label: 'Due within 6 h', value: summary?.dueSoon ?? '—', tint: 'var(--fx-orange)' },
        { label: 'Blocked on missing data', value: summary?.blocked ?? '—', tint: 'var(--fx-purple)' },
        { label: 'Delivered', value: summary?.delivered ?? '—', tint: 'var(--fx-green)' },
      ]}>
      {/* Where the trouble is — by destination region and by cause */}
      {(regions.length > 0 || reasons.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 14, marginBottom: 14 }}>
          <div style={panel}>
            <div style={panelHead}>Destination regions {pick?.kind === 'region' && <button type="button" style={clearBtn} onClick={() => setPick(null)}>clear filter</button>}</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...P.table, minWidth: 0 }}>
                <thead><tr>{['Region', 'Pending', 'Overdue', 'Sent', 'Success', 'Main reason'].map((h) => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {regions.map((r) => (
                    <tr key={r.key} style={{ cursor: 'pointer', background: pick?.kind === 'region' && pick.key === r.key ? 'var(--fx-accent-soft)' : undefined }}
                      onClick={() => setPick(pick?.key === r.key ? null : { kind: 'region', key: r.key, label: r.place || r.key })}>
                      <td style={P.td}>
                        <b>{r.place || 'Unknown'}</b><br />
                        <span style={{ fontSize: 11, color: 'var(--fx-text-3)' }}>state {r.stateCode || '?'} · district {r.districtCode || '?'}</span>
                      </td>
                      <td style={{ ...P.td, textAlign: 'right', fontWeight: 700, color: r.pending ? 'var(--fx-orange)' : 'var(--fx-green)' }}>{r.pending}</td>
                      <td style={{ ...P.td, textAlign: 'right', fontWeight: 700, color: r.overdue ? 'var(--fx-red)' : 'var(--fx-text-3)' }}>{r.overdue}</td>
                      <td style={{ ...P.td, textAlign: 'right' }}>{r.delivered}</td>
                      <td style={{ ...P.td, textAlign: 'right' }}>{r.successPct}%</td>
                      <td style={{ ...P.td, fontSize: 12, color: 'var(--fx-text-2)' }}>{r.pending ? r.reason : 'All clear ✓'}</td>
                    </tr>
                  ))}
                  {regions.length === 0 && <tr><td colSpan={6} style={P.emptyTd}>No trips in this window</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={panel}>
            <div style={panelHead}>Reasons {pick?.kind === 'reason' && <button type="button" style={clearBtn} onClick={() => setPick(null)}>clear filter</button>}</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...P.table, minWidth: 0 }}>
                <thead><tr>{['Reason', 'Calls', 'Overdue', 'Regions'].map((h) => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {reasons.map((r) => (
                    <tr key={r.code} style={{ cursor: 'pointer', background: pick?.kind === 'reason' && pick.key === r.code ? 'var(--fx-accent-soft)' : undefined }}
                      onClick={() => setPick(pick?.key === r.code ? null : { kind: 'reason', key: r.code, label: r.label })}>
                      <td style={P.td}>{r.label}</td>
                      <td style={{ ...P.td, textAlign: 'right', fontWeight: 700 }}>{r.count}</td>
                      <td style={{ ...P.td, textAlign: 'right', color: r.overdue ? 'var(--fx-red)' : 'var(--fx-text-3)' }}>{r.overdue}</td>
                      <td style={{ ...P.td, fontSize: 12, color: 'var(--fx-text-2)' }}>{r.regions.length}</td>
                    </tr>
                  ))}
                  {reasons.length === 0 && <tr><td colSpan={4} style={P.emptyTd}>Nothing pending ✓</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {pick && (
        <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)', marginBottom: 10 }}>
          Showing only: <b style={{ color: 'var(--fx-text)' }}>{pick.label}</b>
          <button type="button" style={{ ...clearBtn, marginLeft: 8 }} onClick={() => setPick(null)}>show all</button>
        </div>
      )}

      <ReportTable<IfmsQueueItem> title="IFMS Queue" columns={COLS} rows={shown} loading={loading} error={error}
        initialSort={{ key: 'dueAt', dir: 1 }} onRowClick={(r) => navigate(`/fleet/trips/${r.tripId}`)}
        emptyText="Everything has reached IFMS ✓" pageSize={25} />

      {detail && (
        <EventDetail tripId={detail.tripId} apiName={detail.api}
          canReconcile={can(user, 'ifms')}
          onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); load(); }} />
      )}
    </ReportShell>
  );
}

/**
 * Everything known about one reporting event.
 *
 * The attempt history is the point of it: the summary row only keeps the latest
 * try, and when somebody asks why a report took six hours the answer is in the
 * ones before it.
 */
function EventDetail({ tripId, apiName, canReconcile, onClose, onChanged }: {
  tripId: string; apiName: IfmsQueueItem['api']; canReconcile: boolean;
  onClose: () => void; onChanged: () => void;
}) {
  const [ev, setEv] = useState<IfmsEvent | null>(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getIfmsEvent(tripId, apiName)
      .then((r) => { if (alive) setEv(r.data.event); })
      .catch((e) => { if (alive) setErr(apiError(e, 'Could not load this event')); });
    return () => { alive = false; };
  }, [tripId, apiName]);

  const decide = async (accepted: boolean) => {
    if (!ev?.logId) return;
    setBusy(true); setErr('');
    try {
      await reconcileIfms(ev.logId, accepted, note.trim());
      onChanged();
    } catch (e) {
      setErr(apiError(e, 'Could not record that'));
    } finally { setBusy(false); }
  };

  const row = (k: string, v: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid var(--fx-border)' }}>
      <span style={{ width: 150, flexShrink: 0, fontSize: 12, color: 'var(--fx-text-2)' }}>{k}</span>
      <span style={{ fontSize: 12.5, color: 'var(--fx-text)', wordBreak: 'break-word' }}>{v}</span>
    </div>
  );

  return (
    <Modal title={ev ? `${ev.label} — ${ev.tripNumber}` : 'IFMS event'} width={880} onClose={onClose}>
      {err && <div style={{ ...P.btnGhost, display: 'block', color: 'var(--fx-red)', marginBottom: 10 }}>{err}</div>}
      {!ev && !err && <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)' }}>Loading…</div>}

      {ev && (
        <>
          {row('Status', <Tag text={STATUS_TEXT[ev.status]} tone={STATUS_TONE[ev.status]} />)}
          {row('Vehicle', ev.vehicleNumber || '—')}
          {row('Attempts', `${ev.attempts}${ev.firstAttemptAt ? ` · first ${fmtDT(ev.firstAttemptAt)}` : ''}`)}
          {ev.nextRetryAt && row('Next try', fmtDT(ev.nextRetryAt))}
          {ev.ackAt && row('Accepted', `${fmtDT(ev.ackAt)}${ev.ackRef ? ` · ref ${ev.ackRef}` : ''}`)}
          {ev.idempotencyKey && row('Event key', <code style={{ fontSize: 11.5 }}>{ev.idempotencyKey}</code>)}
          {ev.reconcileNote && row('Reconciled', ev.reconcileNote)}

          {ev.missing.length > 0 && (
            <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--fx-orange-soft)', border: '1px solid var(--fx-orange)' }}>
              <b style={{ fontSize: 12.5, color: 'var(--fx-orange)' }}>Fill these in before this can be sent</b>
              <ul style={{ margin: '6px 0 0 18px', fontSize: 12.5, color: 'var(--fx-text)' }}>
                {ev.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
              <div style={{ fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 6 }}>
                Saving the correction queues this report again by itself — no need to come back here.
              </div>
            </div>
          )}

          {ev.status === 'UNKNOWN_OUTCOME' && (
            <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--fx-red-soft)', border: '1px solid var(--fx-red)' }}>
              <b style={{ fontSize: 12.5, color: 'var(--fx-red)' }}>This may already have reached IFMS</b>
              <div style={{ fontSize: 12, color: 'var(--fx-text)', margin: '6px 0 10px' }}>
                The reply never came back, so the system will not send it again on its own — doing so could
                enter the same dispatch twice. Check on the IFMS portal, then record what you found.
              </div>
              {canReconcile ? (
                <>
                  <input style={{ ...P.input, marginBottom: 8 }} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Where you checked and what you saw — kept as the evidence" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" style={P.btnPrimary} disabled={busy || note.trim().length < 5}
                      onClick={() => decide(true)}>It is there — mark delivered</button>
                    <button type="button" style={P.btnGhost} disabled={busy || note.trim().length < 5}
                      onClick={() => decide(false)}>Not there — send it again</button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>You do not have the IFMS permission needed to close this.</div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, fontWeight: 700, fontSize: 13 }}>Attempts</div>
          {ev.history.length === 0
            ? <div style={{ fontSize: 12.5, color: 'var(--fx-text-2)', marginTop: 4 }}>Nothing has been sent yet.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--fx-surface-2)' }}>
                    {['#', 'When', 'Took', 'HTTP', 'Outcome', 'Detail'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--fx-text-2)', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ev.history.map((a) => (
                    <tr key={a.attempt} style={{ borderTop: '1px solid var(--fx-border)' }}>
                      <td style={{ padding: '6px 8px' }}>{a.attempt}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDT(a.startedAt)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{a.durationMs != null ? `${a.durationMs} ms` : '—'}</td>
                      <td style={{ padding: '6px 8px' }}>{a.httpStatus ?? '—'}</td>
                      <td style={{ padding: '6px 8px' }}>{a.outcome}</td>
                      <td style={{ padding: '6px 8px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={a.error || ''}>{a.error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          {ev.payload && (
            <>
              <div style={{ marginTop: 16, fontWeight: 700, fontSize: 13 }}>What we send</div>
              <pre style={{ marginTop: 6, padding: 10, background: 'var(--fx-surface-2)', borderRadius: 8, fontSize: 11.5, overflowX: 'auto' }}>
                {JSON.stringify(ev.payload, null, 2)}
              </pre>
            </>
          )}
        </>
      )}
    </Modal>
  );
}

const panel: React.CSSProperties = { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, boxShadow: 'var(--fx-shadow)', overflow: 'hidden' };
const panelHead: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--fx-border)', fontSize: 13.5, fontWeight: 700, color: 'var(--fx-text)' };
const clearBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--fx-accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0 };
